import type {
    QuestionComment,
    QuestionResultData,
    TextAnswer,
} from '@/components/QuestionResultContainer';
import type { User } from '@/types/directory/User';
import type { ReviewAnswer } from '@/types/review/ReviewAnswer';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { QuestionType } from '@/constants/QuestionType';
import { ReviewType } from '@/constants/review/CycleInstance';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { ReviewAnswerStatus } from '@/constants/review/ReviewAnswer';
import { ReviewInstanceStatus } from '@/constants/review/ReviewInstanceStatus';
import { isNonCommentableQuestionType } from '@/utils/question';

interface GetAllReviewersParams {
    reviewInstance?: ReviewInstance;
    users: User[];
}

export const getAllReviewInstanceAssignedUserIds = (
    reviewInstances: ReviewInstance[],
): string[] => {
    const userIds: Set<string> = new Set();

    reviewInstances.forEach((reviewInstance) => {
        // Direct assignedTo on ReviewInstance
        if (reviewInstance.assignedTo) {
            userIds.add(reviewInstance.assignedTo);
        }

        // The manager running the assessment — the reviewer on a manager evaluation, and the only
        // place they are recorded before they answer anything. Without this, getAllReviewers below
        // has no user to match against.
        if (reviewInstance.cycleInstance?.assignedTo) {
            userIds.add(reviewInstance.cycleInstance.assignedTo);
        }

        // Nested in reviewAudienceAssignment (reviewee)
        const audienceAssignment = reviewInstance.reviewAudienceAssignment;
        if (audienceAssignment?.assignedTo) {
            userIds.add(audienceAssignment.assignedTo);
        }

        // Reviewers from reviewAnswers
        reviewInstance.reviewAnswers?.forEach((reviewAnswer) => {
            if (reviewAnswer.assignedTo) {
                userIds.add(reviewAnswer.assignedTo);
            }
        });
    });

    return Array.from(userIds);
};

/**
 * Who replied to a comment, as far as the server names them: it never names the respondent of an anonymous feedback.
 * The replies come from whoever may read the results, not only from the reviewee or the person who launched it.
 */
export const getCommentReplyAuthorIds = (reviewInstance: ReviewInstance): string[] =>
    (reviewInstance.reviewAnswers ?? [])
        .flatMap((reviewAnswer) => reviewAnswer.reviewQuestionAnswers)
        .flatMap((questionAnswer) => questionAnswer.commentReplies ?? [])
        .map((reply) => reply.createdBy)
        .filter((authorId): authorId is string => Boolean(authorId));

/**
 * The reviewers a card counts without showing: everyone an anonymous feedback invited. Undefined for a named
 * feedback, whose reviewers the card shows.
 */
export const getAnonymousReviewerCount = (
    reviewInstance: Pick<ReviewInstance, 'feedbackPrivacy' | 'totalCount'>,
): number | undefined =>
    isAnonymousFeedback(reviewInstance.feedbackPrivacy) ? reviewInstance.totalCount : undefined;

export const getAllReviewers = ({ reviewInstance, users }: GetAllReviewersParams): User[] => {
    if (isAnonymousFeedback(reviewInstance?.feedbackPrivacy)) {
        return [];
    }

    // On a Manager Evaluation the reviewer is the manager who runs the assessment — the cycle
    // instance's assignedTo. The ReviewInstance's own assignedTo is the assessed employee, so
    // reading it here listed the reviewee as their own reviewer. reviewAnswers is not usable
    // either: it stays empty until the manager answers something.
    if (reviewInstance?.cycleInstance?.reviewType === ReviewType.MANAGER_EVALUATION) {
        const managerId = reviewInstance.cycleInstance?.assignedTo;
        return managerId ? users.filter((user) => user.id === managerId) : [];
    }

    const reviewerIds =
        reviewInstance?.reviewAnswers?.map((reviewAnswer) => reviewAnswer.assignedTo) ?? [];

    return users.filter((user) => reviewerIds.includes(user.id));
};

export const getReviewee = ({ reviewInstance, users }: GetAllReviewersParams): User | undefined => {
    return reviewInstance?.reviewAudienceAssignment?.assignedTo
        ? users.find((user) => user.id === reviewInstance?.reviewAudienceAssignment?.assignedTo)
        : undefined;
};

export const getCurrentUser = ({
    reviewInstance,
    users,
}: GetAllReviewersParams): User | undefined => {
    return reviewInstance?.reviewAudienceAssignment?.assignedTo
        ? users.find((user) => user.id === reviewInstance?.reviewAudienceAssignment?.assignedTo)
        : undefined;
};

// A manager assessment is a single-evaluator, multi-step flow, so its progress is measured by the
// stepper (managerAssessmentStep of MANAGER_ASSESSMENT_TOTAL_STEPS) rather than answered evaluators.
const MANAGER_ASSESSMENT_TOTAL_STEPS = 4;
const MAX_PROGRESS_PERCENTAGE = 100;

export const getProgressPercentage = (reviewInstance: ReviewInstance): number => {
    if (reviewInstance.cycleInstance?.reviewType === ReviewType.MANAGER_EVALUATION) {
        if (reviewInstance.status === ReviewInstanceStatus.COMPLETED) {
            return MAX_PROGRESS_PERCENTAGE;
        }
        // managerAssessmentStep is the step the manager is currently on, so completed steps = step - 1.
        const step = reviewInstance.cycleInstance?.managerAssessmentStep ?? 1;
        const completedSteps = Math.max(step - 1, 0);
        return Math.min(
            (completedSteps / MANAGER_ASSESSMENT_TOTAL_STEPS) * MAX_PROGRESS_PERCENTAGE,
            MAX_PROGRESS_PERCENTAGE,
        );
    }

    if (reviewInstance.totalCount === 0) {
        return 0;
    }

    return (reviewInstance.responseCount / reviewInstance.totalCount) * MAX_PROGRESS_PERCENTAGE;
};

/**
 * A 360's results show only the answers their respondents submitted, as its count and its card do: an answer still
 * being written is not one yet. An anonymous 360 carries no status, and the server sends it submitted answers only.
 */
const isShownInResults = (reviewInstance: ReviewInstance, reviewAnswer: ReviewAnswer) =>
    reviewInstance.cycleInstance?.reviewType !== ReviewType.EVALUATION_360 ||
    reviewAnswer.status !== ReviewAnswerStatus.IN_PROGRESS;

interface GetReviewQuestionAnswersParams {
    reviewInstance?: ReviewInstance;
    users: User[];
}

export const getReviewQuestionAnswers = ({
    reviewInstance,
    users,
}: GetReviewQuestionAnswersParams): QuestionResultData[] => {
    if (!reviewInstance?.reviewAnswers) {
        return [];
    }

    const questionMap = new Map<
        string,
        QuestionResultData & {
            answerData: Record<string | number, number>;
            textAnswers: TextAnswer[];
            comments: QuestionComment[];
        }
    >();

    const isAnonymous = isAnonymousFeedback(reviewInstance?.feedbackPrivacy);
    const findUser = (userId?: string | null) =>
        userId ? (users.find((u) => u.id === userId) ?? null) : null;

    const shownAnswers = reviewInstance.reviewAnswers.filter((answer) =>
        isShownInResults(reviewInstance, answer),
    );

    for (const reviewAnswer of shownAnswers) {
        const reviewerUser = isAnonymous ? null : findUser(reviewAnswer.assignedTo);

        for (const questionAnswer of reviewAnswer.reviewQuestionAnswers) {
            const questionId = questionAnswer.reviewQuestionId;
            const existing = questionMap.get(questionId);

            const aggregateAnswer = (answerData: Record<string | number, number>) => {
                const questionType = questionAnswer.reviewQuestion.questionType;
                const isRankingQuestion = questionType === QuestionType.RANKING;
                const isYesNoQuestion = questionType === QuestionType.YES_NO;

                if (
                    isYesNoQuestion &&
                    questionAnswer.boolValue !== undefined &&
                    questionAnswer.boolValue !== null
                ) {
                    const index = questionAnswer.boolValue ? 0 : 1;
                    answerData[index] = (answerData[index] ?? 0) + 1;
                } else if (questionAnswer.selectedOptions.length > 0) {
                    if (isRankingQuestion) {
                        const sortedSelections = [...questionAnswer.selectedOptions].sort(
                            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
                        );
                        const optionsCount = sortedSelections.length;

                        sortedSelections.forEach((selection, index) => {
                            const optionId = selection.reviewQuestionOptionId;
                            const score = optionsCount - index;
                            answerData[optionId] = (answerData[optionId] ?? 0) + score;
                        });
                    } else {
                        for (const selection of questionAnswer.selectedOptions) {
                            const optionId = selection.reviewQuestionOptionId;
                            answerData[optionId] = (answerData[optionId] ?? 0) + 1;
                        }
                    }
                } else if (
                    questionAnswer.intValue !== undefined &&
                    questionAnswer.intValue !== null
                ) {
                    answerData[questionAnswer.intValue] =
                        (answerData[questionAnswer.intValue] ?? 0) + 1;
                }
            };

            const replies: QuestionComment[] = (questionAnswer.commentReplies ?? []).map(
                (reply) => {
                    const isAnonymousRespondent = isAnonymous && reply.authorIsRespondent;

                    return {
                        id: reply.id,
                        reviewQuestionAnswerId: reply.reviewQuestionAnswerId,
                        user: isAnonymousRespondent ? null : findUser(reply.createdBy),
                        text: reply.content,
                        timestamp: reply.createdAt ? new Date(reply.createdAt) : undefined,
                        isAnonymousRespondent,
                    };
                },
            );

            const isNonCommentableQuestion = isNonCommentableQuestionType(
                questionAnswer.reviewQuestion.questionType,
            );

            const answeredAt = questionAnswer.updatedAt
                ? new Date(questionAnswer.updatedAt)
                : undefined;

            const comment: QuestionComment | null =
                questionAnswer.comment && !isNonCommentableQuestion
                    ? {
                          id: questionAnswer.id ?? questionAnswer.reviewQuestionId,
                          reviewQuestionAnswerId: questionAnswer.id,
                          user: reviewerUser,
                          text: questionAnswer.comment,
                          timestamp: answeredAt,
                          isAnonymousRespondent: isAnonymous,
                          replies,
                      }
                    : null;

            const textAnswer: TextAnswer | null =
                !questionAnswer.hasBeenSkipped && questionAnswer.textValue
                    ? {
                          user: reviewerUser,
                          text: questionAnswer.textValue,
                          submittedAt: answeredAt,
                          isAnonymousRespondent: isAnonymous,
                      }
                    : null;

            if (existing) {
                existing.totalCount += 1;
                if (!questionAnswer.hasBeenSkipped) {
                    existing.answeredCount += 1;
                    aggregateAnswer(existing.answerData);
                }
                if (textAnswer) {
                    existing.textAnswers.push(textAnswer);
                }
                if (comment) {
                    existing.comments.push(comment);
                }
            } else {
                const answerData: Record<number, number> = {};
                if (!questionAnswer.hasBeenSkipped) {
                    aggregateAnswer(answerData);
                }
                // Sort reviewQuestionOptions by sortOrder to ensure correct display order
                const sortedQuestion = {
                    ...questionAnswer.reviewQuestion,
                    reviewQuestionOptions: [
                        ...(questionAnswer.reviewQuestion.reviewQuestionOptions ?? []),
                    ].sort((a, b) => a.sortOrder - b.sortOrder),
                };

                questionMap.set(questionId, {
                    question: sortedQuestion,
                    categoryName: '',
                    averagePercentage: 0,
                    answeredCount: questionAnswer.hasBeenSkipped ? 0 : 1,
                    totalCount: 1,
                    answerData,
                    textAnswers: textAnswer ? [textAnswer] : [],
                    comments: comment ? [comment] : [],
                });
            }
        }
    }

    // The questions in the questionnaire's order: the answers arrive in no order of the questionnaire's, and an
    // anonymous feedback's pieces in an order of their own. The answers under each question keep the order received.
    return Array.from(questionMap.values())
        .sort((first, second) => questionnairePosition(first) - questionnairePosition(second))
        .map((data) => ({
            ...data,
            averagePercentage:
                data.totalCount > 0 ? (data.answeredCount / data.totalCount) * 100 : 0,
        }));
};

/** A question without a position follows those that have one, in the order it was first answered. */
const questionnairePosition = ({ question }: QuestionResultData) =>
    question.sortOrder ?? Number.MAX_SAFE_INTEGER;
